import React,{useState,useRef}from"react";
import{X,Bug,Lightbulb,HelpCircle,ImagePlus,Upload,AlertCircle,CheckCircle2}from"lucide-react";
import{BLUE}from"../config";
import{Inp,Txa,Spin}from"./UI";
import * as Graph from"../Graph";

const TYPES=[
  {v:"bug",lb:"Bug Report",Icon:Bug,color:"#DC2626",bg:"#FEF2F2",border:"#FECACA"},
  {v:"enhancement",lb:"Enhancement / Feature",Icon:Lightbulb,color:"#D97706",bg:"#FFFBEB",border:"#FDE68A"},
  {v:"question",lb:"Question / Help",Icon:HelpCircle,color:"#2563EB",bg:"#EFF6FF",border:"#BFDBFE"},
];
const PRI=[{v:"high",lb:"High"},{v:"medium",lb:"Medium"},{v:"low",lb:"Low"}];
const MAX_SHOTS=3;

export default function FeedbackModal({cu,getToken,onClose}){
  const[type,setType]=useState("bug");
  const[title,setTitle]=useState("");
  const[desc,setDesc]=useState("");
  const[pri,setPri]=useState("medium");
  const[shots,setShots]=useState([]); // [{file, preview, url, uploading, error}]
  const[saving,setSaving]=useState(false);
  const[done,setDone]=useState(false);
  const[error,setError]=useState("");
  const fileRef=useRef();

  const addFiles=files=>{
    const arr=Array.from(files).slice(0,MAX_SHOTS-shots.length);
    setShots(s=>[...s,...arr.map(f=>({file:f,preview:URL.createObjectURL(f),url:"",uploading:false,error:""}))]);
  };

  const removeShot=i=>setShots(s=>s.filter((_,idx)=>idx!==i));

  const onDrop=e=>{e.preventDefault();addFiles(e.dataTransfer.files);};
  const onDragOver=e=>e.preventDefault();

  const submit=async()=>{
    if(!title.trim()){setError("Title is required.");return;}
    if(!desc.trim()){setError("Description is required.");return;}
    setError("");setSaving(true);
    try{
      const tok=await getToken();
      // Upload screenshots first
      const urls=[];
      const updated=[...shots];
      for(let i=0;i<updated.length;i++){
        if(!updated[i].file)continue;
        updated[i]={...updated[i],uploading:true};
        setShots([...updated]);
        try{
          const url=await Graph.uploadScreenshot(tok,updated[i].file);
          updated[i]={...updated[i],url,uploading:false};
          urls.push(url);
        }catch(e){
          updated[i]={...updated[i],uploading:false,error:"Upload failed"};
        }
        setShots([...updated]);
      }
      const t=TYPES.find(x=>x.v===type);
      await Graph.submitFeedback({
        title:(t?t.lb+": ":"")+title.trim(),
        feedbackType:type,
        description:desc.trim(),
        submittedBy:cu.email,
        priority:pri,
        screenshotUrls:urls
      });
      setDone(true);
    }catch(e){setError("Submit failed: "+e.message);}
    finally{setSaving(false);}
  };

  const selType=TYPES.find(x=>x.v===type);

  if(done)return(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.45)"}}>
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl p-8 text-center">
        <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-4"/>
        <h2 className="text-lg font-bold text-slate-800 mb-2">Submitted!</h2>
        <p className="text-sm text-slate-500 mb-6">Thank you — an admin will review your {type==="bug"?"bug report":type==="enhancement"?"enhancement request":"question"} shortly.</p>
        <button onClick={onClose} className="px-6 py-2.5 text-sm text-white font-semibold rounded-xl" style={{background:BLUE}}>Close</button>
      </div>
    </div>
  );

  return(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:"rgba(0,0,0,0.45)"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col" style={{maxHeight:"90vh"}}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <p className="font-bold text-slate-800">Report an Issue</p>
            <p className="text-xs text-slate-400 mt-0.5">Submitted as {cu.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={17}/></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Type selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-2">Type</label>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map(({v,lb,Icon,color,bg,border})=>(
                <button key={v} onClick={()=>setType(v)} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-colors" style={type===v?{background:bg,borderColor:border}:{borderColor:"#E2E8F0",background:"white"}}>
                  <Icon size={20} style={{color:type===v?color:"#94A3B8"}}/>
                  <span className="text-xs font-semibold text-center leading-tight" style={{color:type===v?color:"#64748B"}}>{lb}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Title <span className="text-red-500">*</span></label>
            <Inp value={title} onChange={e=>setTitle(e.target.value)} placeholder={type==="bug"?"e.g. Task save fails when no project selected":type==="enhancement"?"e.g. Add email notification for due dates":"e.g. How do I reassign a task?"}/>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 mb-1">Description <span className="text-red-500">*</span></label>
            <Txa value={desc} onChange={e=>setDesc(e.target.value)} rows={4} placeholder={type==="bug"?"Steps to reproduce, what happened, what you expected…":type==="enhancement"?"Describe the feature and why it would help…":"What are you trying to do? What have you tried?"}/>
          </div>

          {/* Priority — bug/enhancement only */}
          {type!=="question"&&(
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-2">Priority</label>
              <div className="flex gap-2">
                {PRI.map(({v,lb})=>(
                  <button key={v} onClick={()=>setPri(v)} className={"flex-1 py-1.5 text-xs font-semibold rounded-lg border transition-colors "+(pri===v?"text-white border-transparent":v==="high"?"border-red-200 text-red-400 hover:bg-red-50":v==="medium"?"border-amber-200 text-amber-500 hover:bg-amber-50":"border-slate-200 text-slate-400 hover:bg-slate-50")} style={pri===v?{background:v==="high"?"#DC2626":v==="medium"?"#D97706":"#64748B"}:{}}>
                    {lb}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Screenshots */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-500 flex items-center gap-1"><ImagePlus size={11}/>Screenshots <span className="text-slate-400 font-normal">(optional, max {MAX_SHOTS})</span></label>
              {shots.length<MAX_SHOTS&&<button onClick={()=>fileRef.current.click()} className="text-xs text-blue-500 hover:underline flex items-center gap-1"><Upload size={11}/>Add image</button>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e=>addFiles(e.target.files)}/>
            {shots.length<MAX_SHOTS&&(
              <div onDrop={onDrop} onDragOver={onDragOver} onClick={()=>fileRef.current.click()} className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors mb-2">
                <ImagePlus size={20} className="mx-auto mb-1 text-slate-300"/>
                <p className="text-xs text-slate-400">Drag & drop or click to upload</p>
              </div>
            )}
            {shots.length>0&&(
              <div className="flex gap-2 flex-wrap">
                {shots.map((s,i)=>(
                  <div key={i} className="relative group">
                    <img src={s.preview} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-200"/>
                    {s.uploading&&<div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg"><Spin/></div>}
                    {s.error&&<div className="absolute inset-0 bg-red-50/90 flex items-center justify-center rounded-lg"><AlertCircle size={14} className="text-red-500"/></div>}
                    <button onClick={()=>removeShot(i)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"><X size={10}/></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error&&<div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 flex items-center gap-2"><AlertCircle size={14}/>{error}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={saving||!title.trim()||!desc.trim()} className="px-5 py-2 text-sm text-white font-semibold rounded-lg disabled:opacity-40 flex items-center gap-2" style={{background:selType?.color||BLUE}}>
            {saving&&<Spin/>}<selType.Icon size={13}/>{type==="bug"?"Report Bug":type==="enhancement"?"Submit Request":"Ask Question"}
          </button>
        </div>
      </div>
    </div>
  );
}
